
import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Notification } from "@/hooks/useNotificationDetail";
import NotificationProductCards from "./detail/NotificationProductCards";
import StatusSelect from "./form/StatusSelect";
import InkoopProductReview from "./form/InkoopProductReview";
import { updateNotificationInDb } from "@/services/notificationUpdateService";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Package, Loader2, Check } from "lucide-react";
import { SelectedOrderProductData } from "@/types/notifications";

interface NotificationViewFormProps {
  notification: Notification;
}

const NotificationViewForm = ({ notification }: NotificationViewFormProps) => {
  const displayPriority = notification.priority === 'normaal' ? '-' : notification.priority;
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [showInkoopReview, setShowInkoopReview] = useState(false);
  const [isSendingInkoop, setIsSendingInkoop] = useState(false);

  // Check if inkoop email was already sent
  const { data: inkoopLogs } = useQuery({
    queryKey: ["inkoop-logs", notification.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("notification_webhook_logs")
        .select("id, created_at, success")
        .eq("notification_id", notification.id)
        .eq("webhook_type", "inkoop_email")
        .order("created_at", { ascending: false });
      return data || [];
    },
  });

  const inkoopAlreadySent = inkoopLogs && inkoopLogs.some(l => l.success);

  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === notification.status) return;
    const oldStatus = notification.status;
    
    try {
      await updateNotificationInDb(
        notification.id,
        notification,
        { status: newStatus },
        user?.id,
        user?.name
      );
      
      queryClient.invalidateQueries({ queryKey: ['notification', notification.id] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success("Status bijgewerkt");

      if (newStatus === 'actie_decoprof') {
        try {
          const { error } = await supabase.functions.invoke('send-decoprof-telegram', {
            body: { notification_id: notification.id, old_status: oldStatus }
          });
          if (error) {
            toast.error("De status is gewijzigd, maar het Telegram bericht kon niet worden verzonden.");
          }
        } catch (telegramError) {
          console.error("Error sending Decoprof Telegram:", telegramError);
          toast.error("De status is gewijzigd, maar het Telegram bericht kon niet worden verzonden.");
        }
      }
    } catch (error) {
      console.error("Error updating notification status:", error);
      toast.error("Fout bij het bijwerken van de status");
    }
  };

  // Convert notification products to SelectedOrderProductData for the review dialog
  const productsForReview: SelectedOrderProductData[] = (notification.products || []).map(p => ({
    sku: p.product_sku,
    ean: (p as any).product_ean || null,
    productTitle: p.product_name,
    variantTitle: null,
    quantity: p.product_quantity,
    color: p.product_color || null,
    imageUrl: (p as any).image_url || null,
    brandTitle: null,
    artikelCode: p.product_bc || null,
    productId: (p as any).id || null,
    productMessage: (p as any).match_notes || "",
    eenheid: p.product_eenheid || null,
  }));

  const handleInkoopClick = () => {
    if (productsForReview.length === 0) {
      toast.error("Geen producten beschikbaar voor inkoop");
      return;
    }
    setShowInkoopReview(true);
  };

  const handleInkoopConfirm = async (updatedProducts: SelectedOrderProductData[]) => {
    setShowInkoopReview(false);
    setIsSendingInkoop(true);

    try {
      // Update products in notification_product_monitoring with edited quantities/colors
      for (const product of updatedProducts) {
        const original = notification.products?.find(p => p.product_sku === product.sku);
        if (original && ((original as any).id)) {
          const { error: updateError } = await supabase
            .from("notification_product_monitoring")
            .update({
              product_quantity: product.quantity,
              product_color: product.color,
            })
            .eq("id", (original as any).id);
          if (updateError) {
            console.error("Error updating product:", updateError);
          }
        }
      }

      // Remove products that were deleted by the user
      const removedProducts = (notification.products || []).filter(
        p => !updatedProducts.find(up => up.sku === p.product_sku)
      );
      for (const removed of removedProducts) {
        if ((removed as any).id) {
          await supabase
            .from("notification_product_monitoring")
            .delete()
            .eq("id", (removed as any).id);
        }
      }

      // Send inkoop email with updated product data directly
      const { error } = await supabase.functions.invoke('send-inkoop-email', {
        body: { 
          notification_id: notification.id,
          products: updatedProducts.map(p => ({
            product_name: p.productTitle,
            product_sku: p.sku,
            product_quantity: p.quantity,
            product_color: p.color,
            product_bc: p.artikelCode,
            product_eenheid: p.eenheid || notification.products?.find(np => np.product_sku === p.sku)?.product_eenheid || "1 ST",
          }))
        }
      });

      if (error) {
        toast.error("Inkoop e-mail kon niet worden verzonden");
      } else {
        toast.success(`Inkoop e-mail verzonden voor ${updatedProducts.length} product(en)`);
        queryClient.invalidateQueries({ queryKey: ["inkoop-logs", notification.id] });
        queryClient.invalidateQueries({ queryKey: ["notification", notification.id] });
      }
    } catch (err) {
      console.error("Error sending inkoop email:", err);
      toast.error("Er is een fout opgetreden bij het verzenden");
    } finally {
      setIsSendingInkoop(false);
    }
  };

  const handleInkoopCancel = () => {
    setShowInkoopReview(false);
  };

  return (
    <>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="type">Type</Label>
            <Input id="type" value={notification.type} readOnly className="bg-muted" />
          </div>
          <div>
            <Label htmlFor="vo_number">VO Nummer</Label>
            <Input id="vo_number" value={notification.vo_number || ""} readOnly className="bg-muted" />
          </div>
        </div>

        <div>
          <Label htmlFor="exact_order_number">Exact Ordernummer</Label>
          <Input id="exact_order_number" value={notification.exact_order_number || ""} readOnly className="bg-muted" />
        </div>

        <StatusSelect 
          status={notification.status}
          onStatusChange={handleStatusChange}
          label="Status"
          required={false}
        />

        <div>
          <Label htmlFor="priority">Prioriteit</Label>
          <Input id="priority" value={displayPriority} readOnly className="bg-muted capitalize" />
        </div>

        {/* Product cards */}
        {notification.products && notification.products.length > 0 && (
          <NotificationProductCards products={notification.products as any} />
        )}

        {/* Inkoop button */}
        {notification.products && notification.products.length > 0 && (
          <div className="pt-2">
            <Button
              variant={inkoopAlreadySent ? "outline" : "default"}
              onClick={handleInkoopClick}
              disabled={isSendingInkoop}
              className="w-full sm:w-auto"
            >
              {isSendingInkoop ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : inkoopAlreadySent ? (
                <Check className="h-4 w-4 mr-2 text-green-600" />
              ) : (
                <Package className="h-4 w-4 mr-2" />
              )}
              {inkoopAlreadySent ? "Inkoop opnieuw versturen" : "Inkoop versturen"}
            </Button>
            {inkoopAlreadySent && inkoopLogs?.[0] && (
              <p className="text-xs text-muted-foreground mt-1">
                Laatst verzonden: {new Date(inkoopLogs[0].created_at).toLocaleString("nl-NL")}
              </p>
            )}
          </div>
        )}
      </div>

      <InkoopProductReview
        open={showInkoopReview}
        onOpenChange={setShowInkoopReview}
        products={productsForReview}
        onConfirm={handleInkoopConfirm}
        onCancel={handleInkoopCancel}
      />
    </>
  );
};

export default NotificationViewForm;
